<template>
  <div
    :class="{
      'smart-input--danger': error,
      'smart-input--success': success,
      'smart-input--focus': isFocus,
      'smart-input--disabled cursor-not-allowed': disabled,
    }"
    class="smart-input__wrapper flex align-start flex-col mx-1"
  >
    <div class="flex flex-row w-full shrink-1">
      <input
        :disabled="disabled"
        :placeholder="placeholder"
        :class="{
          'cursor-not-allowed': disabled,
        }"
        v-model="priceComputed"
        maxlength="25"
        class="smart-input shrink-1 grow-1 rounded input-money"
        v-on:keypress="isNumber(event)"
      />
    </div>

    <label v-if="label" :for="id" class="smart-label text-left mb-1-2">
      {{ label }}
    </label>

    <div class="smart-feedback" v-if="error || success">
      {{ error || success }}
    </div>
  </div>
</template>

<script>
// @flow

export default {
  name: "SmartInputMoney",
  props: {
    placeholder: {
      type: String,
      default: () => null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: () => null,
    },
    id: {
      type: String,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    feedback: {
      type: Object,
      default: () => ({
        type: "",
        text: "",
      }),
    },
    name: {
      type: String,
      default: "",
    },
    error: {
      type: String,
      default: null,
    },
    success: {
      type: String,
      default: null,
    },
    value: {}, // eslint-disable-line
    formKey: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      isFocus: false,
      price: null,
    };
  },
  computed: {
    priceComputed: {
      get: function () {
        if (this.price != null) {
          return this.price;
        }
        return this.value;
      },
      set: function (value) {
        this.price = this.numberThousandFormat(value);
      },
    },
  },
  methods: {
    isNumber: function (evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    numberThousandFormat(initialValue) {
      let postCommaValue = "";
      let beforeCommaValue = "";
      let result = "";
      let replace = ["0", "0", "0", "0", "0", "0", "0", "0", "0", "0"];

      if (initialValue == "") {
        initialValue = 0;
      }

      initialValue = initialValue.toString().replace(",", "").replace(".", "");

      if (initialValue.length <= 15) {
        initialValue = parseInt(initialValue);
      }

      initialValue = initialValue.toString().split("").reverse().join("");

      postCommaValue = replace
        .map((item, index) => {
          if (initialValue[index]) {
            return initialValue[index];
          }
          return item;
        })
        .reverse()
        .join("");

      initialValue = initialValue.replace(".", "");

      if (initialValue.length > 10 && initialValue.length < 30) {
        beforeCommaValue = initialValue
          .replace(".", "")
          .slice(10)
          .split("")
          .reverse()
          .join("")
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".");

        return (result = `${beforeCommaValue},${postCommaValue}`);
      } else {
        return (result = `0,${postCommaValue}`);
      }
    },
    getInputId() {
      if (this.id) {
        return this.id;
      }
      if (this.label) {
        return this.transformID(this.label);
      }
      if (this.placeholder) {
        return this.transformID(this.placeholder);
      }
      return this.formKey;
    },
    transformID(data: string) {
      const lowerCaseLabel = data.toLowerCase();
      return `${lowerCaseLabel.replace(/[^a-zA-Z0-9]/g, "")}-${this.formKey}`;
    },
  },
  watch: {
    priceComputed(value: any, oldValue: any) {
      if (value != oldValue) {
        this.$emit("input", value);
      }
    },
  },
};
</script>

<style lang="scss">
$transition-duration: $transition-sm-medium;
$box-shadow-size: 0 0 0 3px;
$box-shadow-default: $box-shadow-size
  rgba($color-brand-primary, $opacity-low-max);

input.input-money {
  width: 10em;
  max-width: 10rem;
  height: 2rem;
  padding: 0.4rem;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  &:focus {
    outline: none;
    border: 2px solid rgba(0, 0, 0, 0.6);
  }
  &:disabled {
    border: none;
    color: black;
    cursor: text !important;
  }
}

.smart-input {
  transition-property: border-color, box-shadow;
  transition-duration: $transition-duration;
  transition-timing-function: $transition-easing-default;
  &--focus {
    .smart-label {
      color: $color-brand-primary;
    }
    .smart-input__prepend,
    .smart-input__append {
      background-color: rgba($color-brand-primary, 0.08);
      color: rgba($color-brand-primary, 0.87);
      border-color: $color-brand-primary;
    }
  }
  &:not([disabled]) {
    &:focus {
      outline: none;
      border-color: $color-brand-primary;
      box-shadow: $box-shadow-default;
      + .smart-label {
        color: $color-brand-primary;
      }
    }
    &:hover {
      border-color: $color-brand-primary;
    }
  }

  &--danger {
    &.smart-input--focus {
      .smart-label {
        color: $color-ui-danger;
      }
      .smart-feedback {
        color: $color-ui-danger;
      }
      .smart-input {
        box-shadow: $box-shadow-size rgba($color-ui-danger, $opacity-low-max);
      }
    }
    .smart-input {
      border-color: $color-ui-danger;
      &:not([disabled]) {
        &:hover,
        &:focus {
          border-color: $color-ui-danger;
        }
        &:focus {
          box-shadow: $box-shadow-size rgba($color-ui-danger, $opacity-low-max);
          + .smart-label {
            color: $color-ui-danger;
          }
        }
      }
    }
    .smart-label {
      color: $color-ui-danger;
    }
    .smart-feedback {
      color: $color-ui-danger;
    }
  }

  &--success {
    &.smart-input--focus {
      .smart-label {
        color: $color-brand-secondary;
      }
      .smart-feedback {
        color: $color-brand-secondary;
      }
      .smart-input {
        box-shadow: $box-shadow-size rgba($color-ui-danger, $opacity-low-max);
      }
    }
    .smart-input {
      border-color: $color-brand-secondary;
    }
    .smart-label {
      color: $color-brand-secondary;
    }
    .smart-feedback {
      color: $color-brand-secondary;
    }
  }

  &--disabled {
    opacity: 0.6;
  }

  &__wrapper {
    position: relative;

    .mx-input {
      @extend .smart-input;
      box-shadow: none;
      font-size: $font-size-default;
    }
    .mx-input-append {
      width: 40px;
      display: flex;
      align-items: center;
    }
    &.smart-input--danger {
      margin-bottom: 0 !important;
      .mx-input {
        border-color: $color-ui-danger !important;
        @extend .smart-input;
      }
    }
  }
  &__icon {
    position: absolute;
    right: 0;
  }
  &__prepend,
  &__append {
    align-items: center;
    background-color: rgba(202, 202, 202, 0.1);
    border: 1px solid rgba($color-ui-gray, 0.56);
    color: rgba($color-ui-gray, 0.56);
    cursor: default;
    display: inline-flex;
    flex-shrink: 0;
    flex-direction: row;
    min-width: 24px;
    height: 40px;
    padding: 0 $spacing-margin-xxsmall;
    text-align: center;
    transition-property: background-color, border-color, color;
    transition-duration: $transition-duration;
    transition-timing-function: $transition-easing-default;
    white-space: nowrap;
  }
  &__prepend {
    border-right: none;
  }
  &__append {
    border-left: none;
  }
}

.smart-label {
  font-family: $font-family-02;
  color: $color-ui-gray;
  margin-bottom: $spacing-margin-xxxsmall;
  transition: color $transition-duration $transition-easing-default;
  order: -1;
}

.smart-feedback {
  font-family: $font-family-01;
  color: $color-ui-gray;
  margin-top: $spacing-margin-xxxsmall;
  align-self: flex-end;
  font-size: $font-size-paragraph;
}

.smart-input {
  -webkit-appearance: none;
  border: 1px solid rgba($color-ui-gray, 0.56);
  flex-shrink: 1;
  font-family: $font-family-02;
  height: 40px;
  min-width: 24px;
  padding: 0 $spacing-padding-xxsmall;
}

.smart-input::placeholder {
  font-family: $font-family-01;
  color: #000000;
  font-size: 16px;
  opacity: $opacity-low-max;
}
</style>